import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Sparkles, Loader2, Download, RefreshCw, Palette } from "lucide-react";

const APPAREL_OPTIONS = ["tshirt","hoodie","crewneck","longsleeve","tank","sweatpants","joggers","shorts","hat","totebag"];
const STYLE_OPTIONS = ["bold vintage","distressed","clean modern","street","puff","classic","athletic","hand-drawn"];

export default function DesignStudio() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [brief, setBrief] = useState("");
  const [apparel, setApparel] = useState("hoodie");
  const [style, setStyle] = useState("bold vintage");
  const [count, setCount] = useState(3);
  const [busy, setBusy] = useState(false);
  const [concepts, setConcepts] = useState([]);
  const [totalCost, setTotalCost] = useState(0);

  React.useEffect(() => { if (!loading && (!user || !user.is_admin)) nav("/auth"); }, [user, loading, nav]);

  const generate = async () => {
    if (!brief.trim()) { toast.error("Give the assistant a brief first"); return; }
    setBusy(true); setConcepts([]);
    try {
      const r = await api.post("/admin/ai/design-concepts", { brief, apparel_type: apparel, style, count });
      setConcepts(r.data.concepts || []);
      setTotalCost(r.data.estimated_cost_usd || 0);
      const ok = r.data.concepts.filter(c => c.path).length;
      const fail = r.data.concepts.length - ok;
      if (ok > 0) toast.success(`Generated ${ok} concept${ok > 1 ? "s" : ""}${fail ? ` · ${fail} failed` : ""}`);
      else toast.error("All concepts failed — check console");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const download = async (c) => {
    if (!c.path) return;
    try {
      const res = await fetch(`${API_BASE}/api/files/${c.path}?raw=1`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${c.title.replace(/[^\w-]+/g,"_")}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="design-studio-page">
      <div className="flex items-center gap-3 mb-2">
        <Palette size={22} className="text-[color:var(--hb-royal)]"/>
        <h1 className="font-display text-4xl text-[color:var(--hb-navy)]">Design Studio</h1>
      </div>
      <p className="text-sm text-neutral-600 mb-6">Type a brief, get {count} <strong>meaningfully different</strong> concept images. Powered by <code className="bg-neutral-100 px-1">gpt-image-1-mini</code> · ~$0.011 per image · knows your DTF/puff rules and brand style.</p>

      <div className="border border-neutral-200 bg-white p-5 mb-6" data-testid="brief-form">
        <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Brief</label>
        <textarea value={brief} onChange={e => setBrief(e.target.value)} rows={3} placeholder="Example: 'A One United reunion design for the Hampton family, class of 2027, that feels like a family crest but bold enough for a hoodie chest print.'"
                  className="w-full border border-neutral-300 px-3 py-2 text-sm resize-none mb-3" data-testid="brief-input"/>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Apparel</label>
            <select value={apparel} onChange={e => setApparel(e.target.value)} className="w-full border border-neutral-300 px-2 py-2 text-sm" data-testid="apparel-select">
              {APPAREL_OPTIONS.map(a => <option key={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Style</label>
            <select value={style} onChange={e => setStyle(e.target.value)} className="w-full border border-neutral-300 px-2 py-2 text-sm" data-testid="style-select">
              {STYLE_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Concepts (max 3)</label>
            <select value={count} onChange={e => setCount(Number(e.target.value))} className="w-full border border-neutral-300 px-2 py-2 text-sm" data-testid="count-select">
              {[1,2,3].map(n => <option key={n} value={n}>{n} · ~${(n*0.011).toFixed(3)}</option>)}
            </select>
          </div>
        </div>
        <button onClick={generate} disabled={busy || !brief.trim()} className="text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-4 py-2 flex items-center gap-1.5 disabled:opacity-50" data-testid="generate-btn">
          {busy ? <><Loader2 size={12} className="animate-spin"/> Generating (~30s each)…</> : <><Sparkles size={12}/> Generate Concepts</>}
        </button>
      </div>

      {concepts.length > 0 && (
        <>
          <div className="text-xs text-neutral-500 mb-3">Session cost: ${totalCost.toFixed(3)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="concepts-grid">
            {concepts.map((c, i) => (
              <div key={i} className="border border-neutral-200 bg-white overflow-hidden flex flex-col" data-testid={`concept-${i}`}>
                <div className="aspect-square bg-neutral-100 relative flex items-center justify-center">
                  {c.path ? (
                    <img src={`${API_BASE}/api/files/${c.path}?raw=1`} alt={c.title} className="w-full h-full object-cover"/>
                  ) : (
                    <div className="p-4 text-xs text-red-700 text-center">{c.error || "Failed"}</div>
                  )}
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <div className="text-sm font-medium mb-1">{c.title}</div>
                  <div className="text-[11px] text-neutral-500 line-clamp-3">{c.prompt}</div>
                  {c.path && (
                    <button onClick={() => download(c)} className="mt-3 w-full text-xs uppercase tracking-widest border border-neutral-300 py-1.5 flex items-center justify-center gap-1 hover:bg-neutral-50" data-testid={`dl-${i}`}>
                      <Download size={11}/> Download
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <button onClick={generate} disabled={busy} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1.5 flex items-center gap-1" data-testid="regen-btn">
              <RefreshCw size={11}/> Try again with same brief
            </button>
          </div>
        </>
      )}

      <div className="mt-8"><Link to="/admin" className="text-xs text-[color:var(--hb-royal)] uppercase tracking-widest">← Admin home</Link></div>
    </div>
  );
}
